<script setup lang="ts">
import Card from "primevue/card";
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from "vue";
import { useExcursionOccurrenceDates } from "@/components/RegistrationProcess/composables/useExcursionOccurrenceDates.ts";
import type { EventWithLocations, Excursion } from "@/components/RegistrationProcess/types/events.ts";
import { getEventDisplayLocation } from "@/components/RegistrationProcess/utils/eventDisplayLocation";
import { getEventDuration } from "@/components/RegistrationProcess/utils/eventDuration";
import CalendarIcon from "@/components/svg-icons/CalendarIcon.vue";
import SearchChevron from "@/components/svg-icons/SearchChevron.vue";
import { useMobile } from "@/composables/useMobile";
import { useTenantTheming } from "@/composables/useTenantTheming.ts";
import ExcursionCardDetail from "../ExcursionCardDetail/index.vue";
import ExcursionImages from "../ExcursionImages/index.vue";
import { excursionIncludedItemPresets } from "./styles.ts";
import type { Props } from "./types.ts";

defineOptions({
  name: "ExcursionIncludedItem",
});

const modelValue = defineModel<Excursion>({ required: true });
const props = withDefaults(defineProps<Props>(), {
  title: "ExcursionIncludedItem",
});

const { isMobile } = useMobile();
// The storefront color stays on the "View Details" CTA and its chevron. The accent band and all
// card copy follow the flag-off convention: gray band for branded tenants, navy text throughout.
const { secondaryColor, secondaryColorClass } = useTenantTheming();
const svgSecondaryColor = computed<string>(() =>
  secondaryColor.value && secondaryColor.value.length > 0 ? secondaryColor.value : "#007F80",
);

const { formattedDateRange, showOccurrenceDates } = useExcursionOccurrenceDates(modelValue, undefined);

const areDetailsVisible = ref<boolean>(false);

const event = computed<EventWithLocations>(() => modelValue.value?.event ?? {});

const location = computed(() =>
  getEventDisplayLocation(event.value)
    .split(",")
    .slice(0, 2)
    .map((item) => item.trim())
    .join(", "),
);

const eventTitle = computed<string>(() => {
  return event.value?.title ?? "";
});

const duration = computed<string>(() => getEventDuration(event.value));

const simpleDetails = computed<string>(() =>
  [location.value, duration.value].filter((detail) => detail.length > 0).join(" | "),
);

const subcategory = computed<string>(() => event.value.event_subcategory?.name ?? "");

const viewButtonVerbiage = computed<string>(() => (isMobile.value ? "View " : "View Details"));

const handleViewDetailsClick = (): void => {
  areDetailsVisible.value = true;
};

const titleRef = ref<HTMLElement | null>(null);
const isTruncated = ref(false);

let resizeObserver: ResizeObserver | null = null;

const checkTruncation = () => {
  const el = titleRef.value;
  if (!el) return;

  isTruncated.value = el.scrollHeight > el.clientHeight || el.scrollWidth > el.clientWidth;
};

onMounted(async () => {
  await nextTick();
  checkTruncation();

  if (titleRef.value) {
    resizeObserver = new ResizeObserver(() => {
      checkTruncation();
    });
    resizeObserver.observe(titleRef.value);
  }
});

onBeforeUnmount(() => {
  resizeObserver?.disconnect();
});
</script>

<template>
  <ExcursionCardDetail
    v-model="modelValue"
    v-model:visible="areDetailsVisible"
  />
  <div class="px-2 pb-2 w-full md:max-w-[377px]" data-testid="excursion-included-item">
  <Card class="h-full w-full overflow-hidden rounded-none shadow-md" :pt="excursionIncludedItemPresets.card">
    <!-- Top section: image(s) -->
    <template #header>
      <div>
        <div
          class="flex flex-col w-full gap-1 p-4"
          :class="secondaryColor ? 'bg-gray-275' : 'bg-teal-accent'"
        >
          <div class="text-sm font-bold line-clamp-1 text-university-primary"><span data-testid="excursion-included-item-subcategory">{{ subcategory }}</span>
          </div>
        </div>
        <!-- `data-excursion-card-media` is the anchor ExcursionIncludedSection measures to line
             the carousel arrows up with the bottom of the image. -->
        <ExcursionImages data-excursion-card-media :model-value="event.image ?? ''" :image-alt="eventTitle" />
      </div>
    </template>
    <!-- Card body -->
    <template #title><span
        ref="titleRef"
        class="card-title text-university-primary"
        :class="{ 'card-title--mobile': isMobile }"
        :title="isTruncated ? eventTitle : undefined"
        data-testid="excursion-included-item-title"
      >{{ eventTitle }}</span></template>

    <!-- Card body -->
    <template #content>
      <!-- Location + duration: stacked on mobile, single line on desktop -->
      <div data-testid="excursion-included-item-details">
        <div v-if="isMobile" class="flex flex-col gap-1">
          <span v-if="location" class="text-sm text-slate-600">{{ location }}</span>
          <span v-if="duration" class="text-sm text-slate-600">{{ duration }}</span>
        </div>
        <span v-else class="text-sm text-slate-600">{{ simpleDetails }}</span>
      </div>
      <div v-if="showOccurrenceDates" class="flex items-center gap-2 text-sm font-semibold mt-2">
        <CalendarIcon class="w-4 h-4" :style="{ color: svgSecondaryColor }" />
        <span>{{ formattedDateRange }}</span>
      </div>
    </template>

    <!-- Footer -->
    <template #footer>
      <!-- CTA -->
      <div
        class="uppercase font-bold flex items-center p-2 gap-[0.625rem] cursor-pointer"
        :class="[isMobile ? 'justify-start' : 'justify-end', secondaryColorClass]"
        data-testid="excursion-included-item-view-details"
        @click="handleViewDetailsClick()"
      >
        <span>{{ viewButtonVerbiage }}</span>
        <div>
          <SearchChevron rotateDirection="right" :fillColor="svgSecondaryColor" :width="14" :height="9" />
        </div>
      </div>
    </template>
  </Card>
</div>
</template>
<style lang="css" scoped>
  .card-title {
    font-size: 1.05rem;
    font-weight: 600;
    line-height: 1.3;
    letter-spacing: -0.01em;

    display: -webkit-box;
    line-clamp: 3;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 3;

    overflow: hidden;
    text-overflow: ellipsis;

    word-break: break-word;
  }

  .card-title--mobile {
    font-size: 1.1875rem;
    font-weight: 700;
    line-height: 1.26;
  }
</style>
